import { RefObject, useRef, useEffect, memo } from 'react';
import TextSearchList from 'lib/TextSearchList';
import { useClickAway } from 'react-use';
import useTextSearchReducer, {
  setByTextValue,
  clickedOut,
  inputChanged,
  inputFocused,
  toggleDropdown,
} from 'redux-implementations/textSearch';

import { Input, InputAdornment } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { fonts } from 'components/styles/variables';
import { handleChange, handleKeyDown, blurInput } from 'components/Shared/TextSearch/helpers';
import useMakesAndModels from 'lib/hooks/useMakesAndModels';
import { useTranslation } from 'react-i18next';
import { ListItem } from 'types/TextSearch';
import Dropdown from './Dropdown';
import { styles } from './styles';

const useStyles = makeStyles({
  root: {
    flexGrow: 1,
    fontFamily: fonts.default,
    fontStyle: 'normal',
    fontWeight: 400,
    fontSize: '16px',
    lineHeight: '24px',
    letterSpacing: '0.2px',
  },
});

export interface TextSearchProps {
  isMobile?: boolean;
  toggleModal?: () => void;
  setInputText?: (text: string) => void;
  onClick?: () => void;
  onChange?: (item: TextSearchValue) => void;
  value?: TextSearchValue;
}

export type TextSearchValue = {
  make?: string;
  model?: string;
  bodyType?: string;
  searchText?: string;
};

const TextSearch = memo(function TextSearch({
  isMobile = false,
  toggleModal,
  setInputText,
  onClick,
  onChange,
  value,
}: TextSearchProps): JSX.Element {
  const { t } = useTranslation();
  const makesAndModels = useMakesAndModels();
  const textSearchList = new TextSearchList(makesAndModels);
  const searchText = value?.searchText;
  const [state, dispatch] = useTextSearchReducer(textSearchList);

  useEffect(() => {
    dispatch(setByTextValue(searchText || ''));
  }, [searchText, dispatch]);

  const textInputRef: RefObject<HTMLInputElement> = useRef(null);
  function handleClickOutside(): void {
    dispatch(clickedOut);
  }

  useClickAway(textInputRef, handleClickOutside);

  const classes = useStyles();

  function handleSelection(item: ListItem): void {
    if (item.make) {
      onChange?.({
        make: item.make,
        model: item.model,
      });
      return;
    }

    if (item.searchParams.body_type) {
      onChange?.({
        bodyType: item.searchParams.body_type,
      });
    }
  }

  function handleKeyboardSelection(): void {
    if (typeof state.cursor === 'number') {
      const item = state.list[state.cursor];
      handleSelection(item);
      toggleModal?.();
    }
  }

  function onDropdownClick(item: ListItem): void {
    dispatch(inputChanged(item.displayName));
    dispatch(toggleDropdown);
    setInputText?.(item.displayName);
    toggleModal?.();
    handleSelection(item);
  }

  return (
    <div className="search-container" ref={textInputRef}>
      <div className="dropdown-container">
        <Dropdown
          hide={!state.dropDownOpen}
          className={isMobile ? 'modal-dropdown' : ''}
          listItems={state.list}
          queryText={state.textInput}
          cursor={state.cursor}
          onClick={onDropdownClick}
        />
      </div>
      <div className="header-container">
        <Input
          inputProps={{
            'data-testid': 'text-search-input',
          }}
          className={classes.root}
          startAdornment={
            <InputAdornment position="start">
              <div className="search-icon" />
            </InputAdornment>
          }
          disableUnderline={true}
          value={state.textInput}
          onChange={(e): void => {
            handleChange(e, dispatch);
          }}
          onFocus={(): void => {
            dispatch(inputFocused);
            document.addEventListener('click', blurInput);
          }}
          spellCheck={false}
          onKeyDown={(e): void => {
            handleKeyDown(e, state, dispatch, handleKeyboardSelection);
          }}
          placeholder={isMobile ? t('srp.topBar.searchMobile') : t('srp.topBar.searchDesktop')}
          autoFocus={isMobile}
          onClick={onClick}
        />
        {isMobile && <button className="close-button" onClick={toggleModal} />}
      </div>
      {isMobile && !state.textInput && <div className="spacer" />}
      <style jsx>{styles}</style>
      <style jsx>{`
        .dropdown-container {
          top: 50px;
          position: absolute;
          width: 100%;
        }
      `}</style>
    </div>
  );
});

export default TextSearch;
